import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CircleAlertIcon,
  CirclePlusIcon,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  Trash2Icon,
} from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router';
import { Transition } from 'react-transition-group';
import { toast } from 'sonner';

import Accordion from '@/components/atoms/Accordion/Accordion';
import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { EditableText, EditableTextVariant } from '@/components/atoms/EditableText/EditableText';
import Table from '@/components/atoms/Table/Table';
import { EmojiPicker } from '@/components/organisms/EmojiPicker/EmojiPicker';
import { MODAL_DELETE, MODAL_FORM, MODAL_WARN } from '@/components/organisms/Modals/ModalConductor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAccount } from '@/hooks/useAccount';
import { useDeskId } from '@/hooks/useDeskId';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { useRules } from '@/modules/rules/hooks/useRules';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Agent } from '@/shared/types/account';
import { OptionBase } from '@/shared/types/common';
import { actions } from '@/shared/types/permissions';
import { Rule } from '@/shared/types/rules';
import { RootState } from '@/shared/types/state';
import { getRestrictedNames } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { invitationRules } from '@/util/validation/rules/invitation-rules';
import { getEditableTextValidationSchema } from '@/util/validation/schema-builders';

import { useDepartments } from '../../hooks/useDepartments';
import { Department, Form } from '../../models';
import RoutingSelect from '../RoutingSelect/RoutingSelect';

import styles from './DepartmentTile.module.scss';

interface Props {
  department: Department;
  isExpanded: boolean;
  setExpanded: (event, isExpanded: boolean) => void;
  panelId: string;
  setOnSubmit: (panelId: string, fn: () => Promise<void>) => void;
  setDirty: (panelId: string, isDirty: boolean) => void;
  setTriggerValidation: (panelId: string, fn: () => Promise<boolean>) => void;
}
const DepartmentTile = ({
  department,
  isExpanded,
  setExpanded,
  setOnSubmit,
  panelId,
  setDirty,
  setTriggerValidation,
  ref,
}: Props & { ref?: React.Ref<HTMLDivElement> }) => {
  const navigate = useNavigate();
  const bodyRef = useRef(null);
  const deskId = useDeskId();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);

  const formMethods = useForm<Form>({ mode: 'onSubmit' });
  const { agents } = useAccount();
  const { rules } = useRules(deskId);
  const [emojiPicker, setEmojiPicker] = useState(false);

  const canWriteDepartments = useSelector((state: RootState) =>
    getPermissions(state, 'departments', actions.WRITE)
  );

  const rulesConnectedToDepartment = useMemo(() => {
    return rules?.rules?.filter(
      (rule) =>
        rule?.actions?.some((action) => action?.department_id === department.department_id) &&
        rule.status === 'active'
    );
  }, [department.department_id, rules?.rules]);

  const {
    departmentUsers,
    deleteDepartment,
    updateDepartment,
    updateStatus,
    addMembersToDepartment,
    removeMemberFromDepartment,
    addMemberStatus,
    departments,
  } = useDepartments(deskId, department.department_id);
  const {
    reset,
    formState: { isDirty },
    control,
    handleSubmit,
    setValue,
    trigger,
  } = formMethods;
  const { t } = useTranslation();

  const formUsers = useWatch({ control, name: 'users' });
  const formIcon = useWatch({ control, name: 'icon' });

  const onSubmit = useCallback(
    async (data) => {
      const dpt = { department_id: department.department_id, name: department.name, ...data };
      updateDepartment(dpt);

      const addedUserIds = data.users
        .filter((user) => !departmentUsers.find((u) => u.user_id === user?.user_id))
        .map((u) => u.user_id);

      const removedUsers = departmentUsers.filter(
        (user) => !data.users.find((u) => u.user_id === user?.user_id)
      );

      if (addedUserIds.length > 0) {
        addMembersToDepartment({ department_id: department.department_id, user_ids: addedUserIds });
      }

      await Promise.all(
        removedUsers.map(async (user) => {
          return removeMemberFromDepartment({
            department_id: department.department_id,
            user_id: user?.user_id,
          });
        })
      );
    },
    [
      addMembersToDepartment,
      department.department_id,
      department.name,
      departmentUsers,
      removeMemberFromDepartment,
      updateDepartment,
    ]
  );

  usePrompt(isDirty && canWriteDepartments, undefined, undefined, handleSubmit(onSubmit));

  // registering onSubmit to the accordions state
  useEffect(() => {
    setOnSubmit(panelId, handleSubmit(onSubmit));
    setTriggerValidation(panelId, trigger);
  }, [handleSubmit, panelId, setOnSubmit, onSubmit, setTriggerValidation, trigger, isDirty]);

  useEffect(() => {
    setDirty(panelId, isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  useEffect(() => {
    if (departmentUsers && department?.routing) {
      reset({ routing: department.routing, users: departmentUsers, name: department.name });
    }
  }, [departmentUsers, department?.routing, department?.name, reset]);

  useEffect(() => {
    if (!isExpanded && isDirty) {
      reset();
    }
  }, [isDirty, isExpanded, reset]);

  const handleDepartmentDelete = useCallback(() => {
    if (rulesConnectedToDepartment.length > 0) {
      const brainWarning = (
        <AlertWithReferences<Partial<Rule>>
          referenceProp="rule_id"
          references={rulesConnectedToDepartment}
          labelKey="name"
          onRefClick={(id) => {
            dispatch(popModal());
            navigate(`/${slug}/environments/${deskId}/rules/${id}`);
          }}
        >
          <Trans i18nKey="departments.delete_warning_rules" values={{ 0: department?.name }} />
        </AlertWithReferences>
      );

      const warnProps = {
        title: t('common.warning'),
        children: brainWarning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else {
      const deleteProps = {
        subtitle: <Trans i18nKey="departments.delete_warning" values={[department.name]} />,
        confirm: true,
        onDelete: () => {
          deleteDepartment(department.department_id, {
            onSettled: () => {
              dispatch(popModal());
            },
          });
        },
      };
      dispatch(pushModal(MODAL_DELETE, deleteProps));
    }
  }, [
    deleteDepartment,
    department?.department_id,
    department?.name,
    deskId,
    dispatch,
    rulesConnectedToDepartment,
    slug,
    t,
    navigate,
  ]);

  const options =
    formUsers
      ?.map(({ user_id }) => {
        const agent = agents?.find((agent) => agent.agent_id === user_id);
        return agent;
      })
      .filter(Boolean) ?? [];

  const agentOptions = useMemo(() => {
    if (!agents) {
      return;
    }

    const departmentUserIds = new Set(formUsers?.map((user) => user.user_id));

    return agents
      .filter((agent) => !departmentUserIds.has(agent.agent_id))
      .map((agent) => ({ value: agent.agent_id, label: agent.name, description: agent.email }));
  }, [formUsers, agents]);

  const handleMembersAdd = () => {
    const modalProps = {
      title: (
        <>
          {t('departments.add_agents_to')} <span className={styles.info}>{department.name}</span>
        </>
      ),
      primaryButtonText: t('common.add'),

      fields: [
        {
          fieldName: 'users',
          fieldValue: [],
          label: t('common.agents'),
          type: 'autocomplete',
          placeholder: t('common.select_an_option'),
          options: agentOptions,
          rules: invitationRules(t).roles,
        },
      ],

      onCreate: async (data: { users: OptionBase[] }) => {
        try {
          const users = data.users.map((r) => ({
            user_id: r.value,
            department_id: department.department_id,
          }));
          setValue('users', [...formUsers, ...users], { shouldDirty: true });
        } catch (e) {
          toast.error(e.message);
        }
        dispatch(popModal());
      },
    };
    dispatch(pushModal(MODAL_FORM, modalProps));
  };

  const getColumns = useMemo(
    () => [
      {
        Header: t('common.name'),
        accessor: 'name',
        width: 500,
        Cell: (props) => {
          const row: Agent = props.row.original;

          const { avatar, email } = row;
          return (
            <div className={styles.listItem}>
              <Avatar src={avatar} />
              <div className={styles.infoContainer}>
                <span>{props.value}</span>
                <span className={styles.email}>{email}</span>
              </div>
            </div>
          );
        },
      },

      {
        Header: '',
        id: 'menu',
        width: 50,
        Cell: (props) => {
          const row: Agent = props.row.original;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  disabled={!canWriteDepartments}
                  aria-label="Member options"
                >
                  <EllipsisVerticalIcon size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => {
                    const filteredUsers = formUsers.filter((user) => user.user_id !== row.agent_id);
                    setValue('users', filteredUsers, { shouldDirty: true });
                  }}
                >
                  <Trash2Icon size={16} />
                  {t('departments.remove_agent')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [canWriteDepartments, formUsers, setValue, t]
  );

  const defaultStyle = { transition: 'opacity 300ms ease-in-out', opacity: 1 };

  const handleAccordionToggle = () => {
    setExpanded(null, !isExpanded);
  };

  const onTextUpdate = (text: string) => {
    setValue('name', text, { shouldDirty: true });
  };

  // Filters out the current department name from the list of restricted values
  const restrictedValues = getRestrictedNames(departments, department?.name);

  const validationSchema = getEditableTextValidationSchema(
    LENGTH_XS,
    restrictedValues,
    t('departments.department_name')
  );

  const editableTextProps = {
    defaultValue: department.name,
    onSubmit: onTextUpdate,
    variant: '!subtitle1' as EditableTextVariant,
    canEdit: canWriteDepartments && isExpanded,
    validationSchema,
    className: styles.editableText,
  };

  const isConnected = rulesConnectedToDepartment?.length > 0;

  const handleIconClick = (e: React.MouseEvent<HTMLElement>) => {
    // Prevents the emoji picker from opening when the accordion is collapsed
    if (isExpanded) {
      e.stopPropagation();
      setEmojiPicker(true);
    }
  };

  const handleIconKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (isExpanded && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      setEmojiPicker(true);
    }
  };

  const title = (
    <div className={styles.titleWrapper}>
      {/* The title renders inside the accordion's trigger button, which may not hold a <button>. */}
      <span
        role="button"
        tabIndex={isExpanded ? 0 : -1}
        onClick={handleIconClick}
        onKeyDown={handleIconKeyDown}
        className={styles.icon}
        aria-disabled={!isExpanded}
      >
        {formIcon || department?.icon}
      </span>

      <EditableText<typeof validationSchema> {...editableTextProps} />

      {rules && department && (
        <div className={cn(styles.accordionHeader)}>
          <Badge type="secondary" flavour={isConnected ? 'successful' : 'neutral'}>
            {isConnected ? t('status.connected') : t('status.disconnected')}
          </Badge>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Accordion
        onSubmit={handleSubmit(onSubmit)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending' || addMemberStatus === 'pending'}
        expanded={isExpanded}
        handleChange={handleAccordionToggle}
        secondaryButtonText={t('departments.delete_department')}
        onSecondaryButtonClick={handleDepartmentDelete}
        title={title}
        readOnly={!canWriteDepartments}
        ref={ref}
      >
        <Transition nodeRef={bodyRef} in={isExpanded} timeout={300}>
          <>
            {emojiPicker && (
              <EmojiPicker
                className={styles.emojiPicker}
                onClickAway={() => {
                  setEmojiPicker(false);
                }}
                onEmojiClick={({ emoji }) => {
                  setValue('icon', emoji, { shouldDirty: true });
                  setEmojiPicker(false);
                }}
                width="100%"
                height={300}
              />
            )}

            {isExpanded && (
              <div ref={bodyRef} style={defaultStyle}>
                <RoutingSelect
                  control={control}
                  department_id={department.department_id}
                  disabled={!canWriteDepartments}
                />
                <div className={cn(styles.labelContainer, 'mb-2')}>
                  <p className="text-sm leading-5 font-medium text-muted-foreground">
                    {t('common.agents')}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={handleMembersAdd}
                    disabled={!canWriteDepartments}
                  >
                    <CirclePlusIcon
                      color={canWriteDepartments ? 'var(--primary)' : 'var(--muted-foreground)'}
                      size={16}
                    />
                    {t('departments.add_agents')}
                  </Button>
                </div>
                <Table
                  data={options}
                  columns={getColumns}
                  emptyMessage={t('departments.no_agents')}
                  flexLayout
                  noGutters
                  fullWidth
                />
                <p className={cn('text-sm leading-5 font-medium', styles.label, 'mt-4')}>
                  {t('departments.connected_rules')}
                </p>
                {rulesConnectedToDepartment?.map((rule) => {
                  return (
                    <div key={rule.rule_id} className={styles.buttonContainer}>
                      <NavLink
                        to={`/${slug}/environments/${deskId}/rules/${rule.rule_id}`}
                        className={styles.link}
                      >
                        <span className="text-sm leading-5 font-normal">{rule?.name}</span>
                        <ExternalLinkIcon size={16} className="text-muted-foreground" />
                      </NavLink>
                    </div>
                  );
                })}
                {!isConnected && (
                  <Alert variant="warning" className="items-center [&>svg]:translate-y-0">
                    <CircleAlertIcon size={16} />
                    <AlertDescription className="flex items-center justify-between gap-4">
                      <span>{t('departments.no_connected_rule')}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!canWriteDepartments}
                        onClick={() => navigate(`/${slug}/environments/${deskId}/rules`)}
                      >
                        {t('common.connect')}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </>
        </Transition>
      </Accordion>
    </>
  );
};

export default DepartmentTile;
